import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { Loader2 } from 'lucide-react';

/**
 * Mileage Calculator
 * Now automatically geocodes city names via OpenStreetMap Nominatim
 * and fetches the driving distance + duration from the public OSRM server.
 * Falls back to manual distance entry if geocoding / routing fails.
 */

const DIST_MI = { mi: 1, km: 0.621371, m: 0.000621371 };
function toMpg(v, unit) {
  const x = Number(v);
  if (!x) return 0;
  if (unit === 'mpg') return x;
  if (unit === 'mpgUK') return x * 0.832674;
  if (unit === 'kmL') return x * 2.35215;
  if (unit === 'L100km') return 235.215 / x;
  return x;
}

async function geocode(place) {
  const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(place)}`,
    { headers: { 'Accept': 'application/json' } });
  const j = await r.json();
  if (!Array.isArray(j) || !j.length) throw new Error(`Location not found: ${place}`);
  return { lat: parseFloat(j[0].lat), lon: parseFloat(j[0].lon), display: j[0].display_name };
}

async function routeOSRM(a, b) {
  const url = `https://router.project-osrm.org/route/v1/driving/${a.lon},${a.lat};${b.lon},${b.lat}?overview=false`;
  const r = await fetch(url);
  const j = await r.json();
  if (j.code !== 'Ok' || !j.routes?.length) throw new Error('Route not found');
  const rt = j.routes[0];
  return { meters: rt.distance, seconds: rt.duration };
}

// Haversine distance in metres (fallback)
function haversine(a, b) {
  const toRad = d => d * Math.PI / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon/2)**2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function MileageCalc() {
  const [start, setStart] = useState('New York, NY');
  const [end, setEnd] = useState('Boston, MA');
  const [distance, setDistance] = useState(215);
  const [unit, setUnit] = useState('mi');
  const [fuel, setFuel] = useState(28);
  const [fuelUnit, setFuelUnit] = useState('mpg');
  const [price, setPrice] = useState(3.50);
  const [priceUnit, setPriceUnit] = useState('gal');
  const [currency, setCurrency] = useState('$');
  const [hours, setHours] = useState(4);
  const [minutes, setMinutes] = useState(0);
  const [roundTrip, setRoundTrip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [source, setSource] = useState(''); // OSRM | Haversine | manual

  const lookup = async () => {
    if (!start.trim() || !end.trim()) { setStatus('Please enter both locations'); return; }
    setLoading(true); setStatus(''); setSource('');
    try {
      const [a, b] = await Promise.all([geocode(start), geocode(end)]);
      try {
        const rt = await routeOSRM(a, b);
        const meters = rt.meters;
        // update distance and unit
        setUnit('km'); setDistance(+(meters/1000).toFixed(2));
        const totalHrs = rt.seconds / 3600;
        setHours(Math.floor(totalHrs));
        setMinutes(Math.round((totalHrs - Math.floor(totalHrs)) * 60));
        setSource('OSRM driving route'); setStatus(`✓ Route found: ${(meters/1000).toFixed(1)} km · ${(rt.seconds/60).toFixed(0)} min`);
      } catch {
        const meters = haversine(a, b);
        setUnit('km'); setDistance(+(meters/1000).toFixed(2));
        setSource('Haversine straight-line (routing service unavailable)');
        setStatus(`✓ Straight-line distance: ${(meters/1000).toFixed(1)} km (driving distance unavailable)`);
      }
    } catch (e) {
      setStatus('Could not resolve location — ' + e.message);
    } finally { setLoading(false); }
  };

  const c = useMemo(() => {
    const oneWayMiles = Number(distance) * DIST_MI[unit];
    const totalMiles = roundTrip ? oneWayMiles * 2 : oneWayMiles;
    const totalDistUserUnit = roundTrip ? Number(distance) * 2 : Number(distance);
    const mpg = toMpg(fuel, fuelUnit);
    const gallons = mpg > 0 ? totalMiles / mpg : 0;
    const liters = gallons * 3.78541;
    const pricePerGal = priceUnit === 'gal' ? Number(price) : Number(price) * 3.78541;
    const totalCost = gallons * pricePerGal;
    const totalHrs = (Number(hours) || 0) + (Number(minutes) || 0) / 60;
    const avgSpeed = totalHrs > 0 ? totalDistUserUnit / totalHrs : 0;
    const cpm = totalDistUserUnit > 0 ? totalCost / totalDistUserUnit : 0;
    return { totalDistUserUnit, totalMiles, gallons, liters, totalCost, avgSpeed, totalHrs, cpm };
  }, [distance, unit, fuel, fuelUnit, price, priceUnit, hours, minutes, roundTrip]);

  const mapUrl = `https://www.google.com/maps/dir/${encodeURIComponent(start)}/${encodeURIComponent(end)}`;

  return (
    <CalcShell look="cnet" title="Mileage Calculator" subtitle="Estimate fuel used, trip cost, cost per mile and average speed for a trip between two locations.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>From</label><input value={start} onChange={e => setStart(e.target.value)} data-testid="mil-from" placeholder="e.g. New York, NY"/></div>
          <div className="field"><label>To</label><input value={end} onChange={e => setEnd(e.target.value)} data-testid="mil-to" placeholder="e.g. Boston, MA"/></div>
          <div className="flex gap-2 items-center mb-3">
            <button onClick={lookup} disabled={loading} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="mil-lookup">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Looking up route…' : 'Calculate distance & time'}
            </button>
            <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline" data-testid="mil-mapurl">Open in Google Maps ↗</a>
          </div>
          {status && <div className={`text-xs mb-3 ${status.startsWith('✓') ? 'text-emerald-700' : 'text-rose-600'}`} data-testid="mil-status">{status}</div>}
          {source && <div className="text-[11px] text-slate-500 mb-3">via {source} — free open data (OpenStreetMap)</div>}

          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Distance</label><input type="number" step="0.1" value={distance} onChange={e => setDistance(e.target.value)} data-testid="mil-dist"/></div>
            <div className="field"><label>Unit</label>
              <select value={unit} onChange={e => setUnit(e.target.value)} data-testid="mil-unit"><option value="mi">mi — mile</option><option value="km">km — kilometer</option><option value="m">m — meter</option></select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={roundTrip} onChange={e => setRoundTrip(e.target.checked)} data-testid="mil-rt"/> Round trip</label>

          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Fuel Economy</label><input type="number" step="0.1" value={fuel} onChange={e => setFuel(e.target.value)} data-testid="mil-fuel"/></div>
            <div className="field"><label>Fuel Unit</label>
              <select value={fuelUnit} onChange={e => setFuelUnit(e.target.value)} data-testid="mil-fuelunit">
                <option value="mpg">mpg (US)</option>
                <option value="mpgUK">mpg (UK)</option>
                <option value="kmL">km/L</option>
                <option value="L100km">L/100 km</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="field"><label>Fuel Price</label><input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)}/></div>
            <div className="field"><label>Per</label>
              <select value={priceUnit} onChange={e => setPriceUnit(e.target.value)}><option value="gal">gal — US gallon</option><option value="L">L — liter</option></select>
            </div>
            <div className="field"><label>Currency</label>
              <select value={currency} onChange={e => setCurrency(e.target.value)}><option>$</option><option>€</option><option>£</option><option>₹</option><option>¥</option></select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Travel Hours</label><input type="number" min="0" value={hours} onChange={e => setHours(e.target.value)}/></div>
            <div className="field"><label>Minutes</label><input type="number" min="0" max="59" value={minutes} onChange={e => setMinutes(e.target.value)}/></div>
          </div>
        </div>

        <div>
          <div className="result-panel mb-3"><div className="result-title">Total Fuel Cost</div><div className="result-value" data-testid="mil-cost">{currency}{c.totalCost.toFixed(2)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Total Distance</td><td className="text-right">{c.totalDistUserUnit.toFixed(2)} {unit}</td></tr>
            <tr><td>Total Distance (miles)</td><td className="text-right">{c.totalMiles.toFixed(2)} mi</td></tr>
            <tr><td>Fuel Used</td><td className="text-right">{c.gallons.toFixed(3)} gal / {c.liters.toFixed(2)} L</td></tr>
            <tr><td>Cost per {unit}</td><td className="text-right">{currency}{c.cpm.toFixed(3)}</td></tr>
            <tr><td>Travel Time</td><td className="text-right">{c.totalHrs.toFixed(2)} h</td></tr>
            <tr><td>Average Speed</td><td className="text-right font-semibold">{c.avgSpeed.toFixed(1)} {unit}/h</td></tr>
          </tbody></table>
        </div>
      </div>
    </CalcShell>
  );
}
